import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Globe2, Search, ShieldAlert } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { Overline } from "../components/motion";
import { useTranslate } from "../context/TranslateContext";
import { MOVIES } from "../mock/data";
import { SERIES } from "../mock/tv";

const COUNTRIES = ["India", "USA", "UK", "UAE", "Australia"];

export default function WatchGuide() {
  const { t } = useTranslate();
  const [country, setCountry] = useState("India");
  const [q, setQ] = useState("");
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const works = useMemo(() => {
    const movs = MOVIES.map((m) => ({ kind: "movie", id: m.id, title: m.title, language: m.language, sub: t("Film"), avail: m.whereToWatch.map((w) => ({ provider: w.name, access: w.type })) }));
    const sers = SERIES.map((s) => ({ kind: "series", id: s.slug, title: s.title, language: s.language, sub: t(s.format), avail: s.availability.map((a) => ({ provider: a.provider, access: a.access })) }));
    return [...movs, ...sers].filter((w) => !q || w.title.toLowerCase().includes(q.toLowerCase()) || w.language.toLowerCase().includes(q.toLowerCase()));
  }, [q, t]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-paper to-surface">
      <InnerNav />
      <div className="mx-auto max-w-[1100px] px-6 py-14 md:px-12 md:py-16">
        <Overline>Watch Guide</Overline>
        <h1 className="mt-3 font-display text-5xl font-semibold tracking-tight text-cream md:text-7xl">{t("Where can I ")}<span className="italic text-gradient-gold">{t("watch it?")}</span></h1>
        <p className="mt-3 font-body text-ash">{t("Find legal ways to watch any title. Links go to official providers — we never host content. Availability is illustrative and not verified.")}</p>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 rounded-full border border-edge bg-surface px-4 py-2.5">
            <Search className="h-4 w-4 text-ash" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="watchguide-search" placeholder={t("Search a film or series")} className="w-56 bg-transparent font-body text-cream placeholder:text-ash focus:outline-none" />
          </div>
          <div className="flex items-center gap-2 rounded-full border border-edge bg-surface px-4 py-2.5">
            <Globe2 className="h-4 w-4 text-gold" />
            <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="watchguide-country" className="bg-transparent font-body text-cream focus:outline-none">
              {COUNTRIES.map((c) => <option key={c} value={c} className="bg-surface">{t(c)}</option>)}
            </select>
          </div>
        </div>

        <div className="mt-8 space-y-3" data-testid="watchguide-list">
          {works.map((w) => (
            <div key={`${w.kind}-${w.id}`} className="rounded-2xl border border-edge bg-surface/40 p-5" data-testid={`wg-${w.id}`}>
              <div className="flex items-center justify-between gap-4">
                <Link to={w.kind === "movie" ? `/movie/${w.id}` : `/series/${w.id}`} className="font-display text-xl text-cream hover:text-gold">{w.title}</Link>
                <span className="font-body text-xs text-ash">{w.language} · {w.sub}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {w.avail.map((a, i) => (
                  <a key={i} href="#" onClick={(e) => e.preventDefault()} className="inline-flex items-center gap-2 rounded-full border border-edge bg-surface px-3 py-1.5 font-body text-xs text-cream hover:border-gold">
                    {a.provider} <span className="text-ash">· {t(a.access)}</span>
                  </a>
                ))}
              </div>
              <div className="mt-3 inline-flex items-center gap-1.5 font-body text-[0.65rem] text-saffron"><ShieldAlert className="h-3 w-3" /> {t("Not verified")} · {t("official providers only")} · {t(country)}</div>
            </div>
          ))}
          {works.length === 0 && <p className="py-16 text-center font-body text-ash">{t("No titles found.")}</p>}
        </div>
      </div>
    </div>
  );
}
